/**
 * Per-screen loading skeletons. Polish queue task 4 of 4.
 *
 * The old PageLoadingSpinner shows a centered pulse with optional
 * "Loading X…" copy. That works for sub-second loads but reads as
 * "waiting" on slower ones. Skeletons show "here's the shape of what's
 * coming" — reduces perceived latency and keeps the page from jumping
 * around when data lands.
 *
 * Each export here matches a specific top-traffic screen's layout
 * closely enough that the swap from skeleton → real content doesn't
 * shift the viewport noticeably. Don't generalize these into a single
 * "GenericSkeleton" — that loses the point.
 */

import { Skeleton } from "@/components/ui/skeleton";

/**
 * Calendar page — toolbar row + main grid surface. The grid itself
 * is a single tall pulse since the week/day/month view changes shape
 * and we don't know which is active here. Matches the
 * h-[calc(100vh-120px)] flex-col layout used in Calendar.tsx.
 */
export function CalendarSkeleton() {
  return (
    <div className="h-[calc(100vh-120px)] flex flex-col space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Skeleton className="h-8 w-28" />
          <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-14 rounded-md" />
            <Skeleton className="h-8 w-8 rounded-md" />
          </div>
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-9 w-[130px] rounded-md" />
          <Skeleton className="h-9 w-20 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
        </div>
      </div>
      <Skeleton className="flex-1 w-full rounded-lg" />
    </div>
  );
}

/**
 * IncomeReports page — header (title + year picker + export buttons)
 * then a tall card area for monthly summary + quarterly estimates +
 * annual report. Single tall pulse for the body since the page is
 * mostly a long-scroll report rather than discrete cards.
 */
export function IncomeReportsSkeleton() {
  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-7 w-44" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-[180px] rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      </div>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-border bg-card p-4 space-y-2"
          >
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-7 w-24" />
          </div>
        ))}
      </div>
      <Skeleton className="h-96 w-full rounded-lg" />
    </div>
  );
}

/**
 * ClientDetail page — back button, profile header card, then a tab
 * row + content area. The detail view has lots of variability so we
 * keep the body skeleton simple (one tall pulse + two stat tiles)
 * rather than try to predict which panel will render.
 */
export function ClientDetailSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-20 rounded-md" />
      <div className="rounded-lg border border-border bg-card p-6 space-y-4">
        <div className="space-y-2">
          <Skeleton className="h-7 w-48" />
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-28" />
          </div>
        </div>
      </div>
      <div className="grid gap-3 sm:gap-4 grid-cols-2 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-lg border border-border bg-card p-4 space-y-2"
          >
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-7 w-12" />
          </div>
        ))}
      </div>
      <Skeleton className="h-10 w-full max-w-md rounded-md" />
      <Skeleton className="h-80 w-full rounded-lg" />
    </div>
  );
}

/**
 * Workouts page — header row + grid of 6 template cards (matches the
 * grid-cols-1 sm:2 lg:3 layout in Workouts.tsx).
 */
export function WorkoutsListSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div className="space-y-2">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <Skeleton className="h-9 w-full max-w-sm" />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <WorkoutCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

function WorkoutCardSkeleton() {
  return (
    <div className="border border-border/60 rounded-lg bg-card p-4 space-y-3">
      <div className="flex items-start justify-between">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-4 rounded" />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-10" />
      </div>
      <Skeleton className="h-3 w-3/4" />
    </div>
  );
}

/**
 * Programs page — header + grid of 6 program cards. Matches Programs.tsx
 * grid-cols-1 md:2 lg:3 layout.
 */
export function ProgramsListSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-4 w-56" />
        </div>
        <Skeleton className="h-9 w-32 rounded-md" />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="border border-border/60 rounded-lg bg-card p-5 space-y-3"
          >
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-3/4" />
            <div className="flex gap-2 pt-2">
              <Skeleton className="h-5 w-12 rounded" />
              <Skeleton className="h-5 w-16 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Clients page — header + filter row + table with 8 rows. The table
 * width fills the container so we don't need to match exact column
 * widths; just create the visual rhythm.
 */
export function ClientsTableSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div className="space-y-2">
          <Skeleton className="h-7 w-32" />
          <Skeleton className="h-4 w-40" />
        </div>
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <div className="flex items-center gap-3">
        <Skeleton className="h-9 w-full max-w-xs" />
        <Skeleton className="h-9 w-32 rounded-md" />
      </div>
      <div className="border border-border/60 rounded-lg overflow-hidden">
        {/* Table header row */}
        <div className="grid grid-cols-6 gap-4 px-4 py-3 bg-muted/30 border-b border-border/60">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-3 w-16" />
          ))}
        </div>
        {/* Rows */}
        {Array.from({ length: 8 }).map((_, rowIdx) => (
          <div
            key={rowIdx}
            className="grid grid-cols-6 gap-4 px-4 py-3 border-b border-border/40 last:border-0 items-center"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 w-24" />
            </div>
            {Array.from({ length: 5 }).map((_, colIdx) => (
              <Skeleton key={colIdx} className="h-4 w-20" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
